import { Component, Inject, TemplateRef } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Sort } from '@angular/material/sort';
import { MatSortModule } from '@angular/material/sort';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { OrderService } from '../../services/orders/order.service';
import { Router } from '@angular/router';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { OrderCardComponent } from '../../ui/molecules/order-card/order-card.component';
import { finalize } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { TablesService } from '../../services/tables/tables.service';
import { TableInfo } from '../../services/tables/interfaces/table.interface';

export interface Dessert {
  /*orderId: string;
  date: string;
  customerName: string;
  location: string;
  amount: number;
  status: string;*/
  order: { id: number, uuid: number };
  uuid: string;
  date: string;
  totalItems: number;
  table: { name: string, uuid: number };
  status: string;
  amount: number;
}

// ── Dialog: seleccionar mesa cuando hay múltiples idle ──────────────────────
@Component({
  selector: 'app-table-picker-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule],
  template: `
    <h2 mat-dialog-title>Seleccionar mesa</h2>
    <mat-dialog-content>
      <div class="table-list">
        <button *ngFor="let t of data.tables" mat-stroked-button class="table-btn"
          (click)="pick(t)">{{ t.name }}</button>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="dialogRef.close()">Cancelar</button>
    </mat-dialog-actions>
  `,
  styles: [`.table-list{display:flex;flex-direction:column;gap:8px;min-width:220px;padding:4px 0;} .table-btn{justify-content:flex-start;}`]
})
export class TablePickerDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<TablePickerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { tables: TableInfo[] },
  ) {}
  pick(t: TableInfo) { this.dialogRef.close(t); }
}
// ────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-orders-list',
  standalone: true,
  imports: [CommonModule, MatSortModule, MatDialogModule, MatIconModule, MatButtonModule, MatTooltipModule, PaginationComponent, NgbModule, NgTemplateOutlet, LoadingSpinnerComponent, OrderCardComponent, ConfirmDialogComponent, TablePickerDialogComponent],
  templateUrl: './orders-list.component.html',
  styleUrls: ['./orders-list.component.css']
})
export class OrdersListComponent {
  active = 1;
  page: number = 1;
  totalRows: number = 20;
  totalPage: number = 0;
  pageSize: number = 20;
  serverPaginated = false;
  selectedRange: 'today' | 'week' | 'month' = 'today';
  // 0 = mes en curso, -1 = mes anterior
  monthOffset: number = 0;
  // Filtro por tipo de mesa (mantener filtros de Mesas)
  activeType: 'ALL' | 'LOCAL' | 'DELIVERY' | 'PICKUP' | 'FOODTRUCK' = 'ALL';
  private readonly LS_KEYS = {
    tableType: 'ui.activeTableType',
    ordersRange: 'ui.ordersRange',
    ordersMonthOffset: 'ui.ordersMonthOffset'
  } as const;
  allData: any = [];
  openData: any = [];
  closedData: any = [];
  pendingPaymentData: any = [];
  boxActive: Boolean = false;
  isLoading = false;
  isAdmin = false;

  constructor(
    private modalService: NgbModal,
    private orderService: OrderService,
    private router: Router,
    private keycloak: KeycloakService,
    private dialog: MatDialog,
    private tablesService: TablesService,
  ) {
    this.orderData = [];
    try {
      const rolesA = (this.keycloak?.getUserRoles ? this.keycloak.getUserRoles() : []) as string[];
      const rolesB = (this.keycloak?.getUserRoles ? this.keycloak.getUserRoles(true) : []) as string[];
      const all = [...new Set([...(rolesA || []), ...(rolesB || [])].map(r => (r || '').toLowerCase()))];
      this.isAdmin = all.includes('admin');
      const inst: any = (this.keycloak as any)?.getKeycloakInstance?.();
      if (inst?.hasRealmRole && inst.hasRealmRole('admin')) this.isAdmin = true;
    } catch { }
  }

  forceDelete(row: any): void {
    const orderUuid = String(row?.order?.uuid || row?.uuid || '');
    if (!orderUuid) return;
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Eliminar orden',
        message: `¿Eliminar la orden #${row?.order?.id ?? ''}? Esta acción no se puede deshacer.`,
        confirmLabel: 'Eliminar',
        cancelLabel: 'Cancelar',
        confirmColor: 'warn',
      },
    });
    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.orderService.forceDeleteOrder(orderUuid).subscribe({
        next: () => this.fetchOrders(),
        error: (e) => console.error('[OrdersList] forceDelete error', e),
      });
    });
  }

  createNewOrder(): void {
    this.tablesService.getTables().subscribe({
      next: (tables: TableInfo[]) => {
        // Filtrar por tipo activo y estado idle (sin orden abierta)
        const isIdle = (t: TableInfo) => {
          const s = (t.status || '').toUpperCase();
          return s === 'IDLE' || s === 'FREE' || s === 'AVAILABLE' || s === '';
        };
        const matchesType = (t: TableInfo) => {
          if (this.activeType === 'ALL') return true;
          return this.normalizeTableType(t.type, t.name) === this.activeType;
        };
        const candidates = tables.filter(t => matchesType(t) && isIdle(t));
        if (candidates.length === 0) {
          alert('No hay mesas disponibles para crear una nueva orden.');
          return;
        }
        if (candidates.length === 1) {
          this.navigateToNewOrder(candidates[0]);
          return;
        }
        // Múltiples mesas idle: mostrar selector
        const ref = this.dialog.open(TablePickerDialogComponent, {
          data: { tables: candidates },
          width: '320px',
        });
        ref.afterClosed().subscribe((table: TableInfo | undefined) => {
          if (table) this.navigateToNewOrder(table);
        });
      },
      error: () => alert('No se pudieron cargar las mesas.'),
    });
  }

  private navigateToNewOrder(table: TableInfo): void {
    const type = this.normalizeTableType(table.type, table.name);
    if (type === 'FOODTRUCK') {
      this.router.navigate([`/admin/foodtruck/${table.uuid}`]);
    } else {
      this.router.navigate([`/admin/orders-edit/${table.uuid}/new`]);
    }
  }

  ngOnInit(): void {
    this.isLoading = true;
    // Restaurar selección desde localStorage
    try {
      const savedType = localStorage.getItem(this.LS_KEYS.tableType);
      if (savedType === 'ALL' || savedType === 'LOCAL' || savedType === 'DELIVERY' || savedType === 'PICKUP' || savedType === 'FOODTRUCK') {
        this.activeType = savedType as typeof this.activeType;
      }
      const savedRange = localStorage.getItem(this.LS_KEYS.ordersRange);
      if (savedRange === 'today' || savedRange === 'week' || savedRange === 'month') {
        this.selectedRange = savedRange as typeof this.selectedRange;
      }
      const savedOffsetRaw = localStorage.getItem(this.LS_KEYS.ordersMonthOffset);
      const savedOffset = savedOffsetRaw !== null ? Number(savedOffsetRaw) : null;
      if (savedOffset === 0 || savedOffset === -1) {
        this.monthOffset = savedOffset;
      }
    } catch { }
    this.fetchOrders();
  }

  dropdown_item1 = {
    select: 'Filter',
    formControl: true,
    value: ['Filter', 'Date']
  }

  dropdown_item2 = {
    select: 'Newest',
    formControl: true,
    value: ['Newest', 'Oldest']
  }
  openCenter(content: TemplateRef<any>) {
    this.modalService.open(content, { centered: true });
  }

  contantHead = {
    title: 'Order List',
    desc: 'Lorem ipsum  dolor sit amet',
    title_path: 'Dashboard'
  }
  desserts: Dessert[] = [];
  /*desserts: Dessert[] = [
    {
      orderId: '#5552375',
      date: '26 January 2024, 02:12 AM',
      customerName: 'Emilia Johanson',
      location: '67 St. John’s Road London',
      amount: 251.16,
      status: 'PENDING',
    },
    {
      orderId: '#5552356',
      date: '27 January 2024, 03:12 AM',
      customerName: 'Rendy Greenlee',
      location: '32 The Green London',
      amount: 44.99,
      status: 'PENDING',
    },
    {
      orderId: '#5552388',
      date: '27 January 2024, 02:15 AM',
      customerName: 'Jessica Wong',
      location: '11 Church Road London',
      amount: 24.17,
      status: 'DELIVERED',
    },
    {
      orderId: '#5552323',
      date: '26 January 2024, 02:12 AM',
      customerName: 'Veronica',
      location: '21 King Street London',
      amount: 74.92,
      status: 'DELIVERED',
    },
    {
      orderId: '#5552322',
      date: '27 January 2024, 03:12 AM',
      customerName: 'Samantha Bake',
      location: '79 The Drive London',
      amount: 22.18,
      status: 'PENDING',
    },
    {
      orderId: '#5552358',
      date: '27 January 2024, 02:15 AM',
      customerName: 'David Horiso',
      location: '981 St. John’s Road London',
      amount: 24.17,
      status: 'DELIVERED',
    },
    {
      orderId: '#5552311',
      date: '26 January 2024, 02:12 AM',
      customerName: 'Olivia Shine',
      location: '35 Station Road London',
      amount: 82.46,
      status: 'PENDING',
    },
    {
      orderId: '#5552358',
      date: '27 January 2024, 02:15 AM',
      customerName: 'David Horiso',
      location: '981 St. John’s Road London',
      amount: 24.17,
      status: 'DELIVERED',
    },
    {
      orderId: '#5552351',
      date: '27 January 2024, 03:12 AM',
      customerName: 'James WItcwicky',
      location: 'Corner Street 5th London',
      amount: 164.52,
      status: 'CANCELED',
    },
    {
      orderId: '#5552349',
      date: '27 January 2024, 02:15 AM',
      customerName: 'Roberto Carlo',
      location: '544 Manor Road London',
      amount: 24.17,
      status: 'DELIVERED',
    }
  ]; */

  orderData: any[]; //Dessert[];

  sortData(sort: Sort) {
    const data = this.desserts.slice();
    if (!sort.active || sort.direction === '') {
      this.orderData = data;
      return;
    }

    this.orderData = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        // case 'orderId': return compare(a.orderId, b.orderId, isAsc);
        case 'date': return compare(a.date, b.date, isAsc);
        // case 'customerName': return compare(a.customerName, b.customerName, isAsc);
        // case 'location': return compare(a.location, b.location, isAsc);
        case 'amount': return compare(a.amount, b.amount, isAsc);
        case 'status': return compare(a.status, b.status, isAsc);
        case 'table ': return compare(a.table.name, b.table.name, isAsc);
        default: return 0;
      }
    });
    this.allData = this.paginator(this.orderData, this.page, this.totalRows);
  }

  pageChange(e: number) {    //  Page Change function
    this.page = Number(e) || 1;
    if (this.serverPaginated) {
      this.fetchOrders();
    } else {
      this.allData = this.paginator(this.orderData, this.page, this.totalRows);
      this.totalPage = this.allData.total_pages;
    }
  }

  onPageSizeChange(size: number): void {
    this.totalRows = size;
    this.pageSize = size;
    this.page = 1;
    if (this.serverPaginated) {
      this.fetchOrders();
    } else {
      this.rebuildDatasets(this.orderData);
    }
  }

  paginator(items: any, current_page: number, per_page_items: number) {
    let page = Number(current_page) || 1,
      per_page = Number(per_page_items) || 10,
      offset = (page - 1) * per_page,

      paginatedItems = items.slice(offset).slice(0, per_page_items),
      total_pages = Math.ceil(items.length / per_page);

    return {
      page: page,
      per_page: per_page,
      pre_page: page - 1 ? page - 1 : null,
      next_page: (total_pages > page) ? page + 1 : null,
      total: items.length,
      total_pages: total_pages,
      data: paginatedItems
    };
  }

  // Data helpers for current active tab
  get currentData(): any {
    if (this.active === 2) return this.openData;
    if (this.active === 3) return this.closedData;
    if (this.active === 4) return this.pendingPaymentData;
    return this.allData;
  }

  get currentTotalPages(): number {
    return Number(this.currentData?.total_pages ?? this.totalPage ?? 1) || 1;
  }

  get currentPageCount(): number {
    return Number(this.currentData?.data?.length ?? 0) || 0;
  }

  get currentTotalCount(): number {
    return Number(this.currentData?.total ?? 0) || 0;
  }

  checkUncheckAll() {
    if (this.boxActive) {
      this.boxActive = false;
    } else {
      this.boxActive = true;
    }
  }

  navigateToOrderEdit(row: any) {
    if (!row) return;
    const tableUuid: string | null = row?.table?.uuid ?? null;
    const typeNorm = this.normalizeTableType(row?.table?.type, row?.table?.name);
    const orderUuid: string = String(row?.order?.uuid || '') || '';
    const orderHashId: string = String(row?.orderHashId || orderUuid || row?.orderId || row?.id || '') || '';

    if (typeNorm === 'FOODTRUCK' && tableUuid) {
      this.router.navigate([`/admin/foodtruck/${tableUuid}`], { queryParams: { orderHashId } });
      return;
    }

    const safeTable = tableUuid || 'no-table';
    this.router.navigate([`/admin/orders-edit/${safeTable}/${orderUuid}`], { queryParams: { fromList: '1' } });
  }

  // ---------- Range filters (UTC) and server pagination support ----------
  onRangeChange(range: 'today' | 'week' | 'month') {
    this.selectedRange = range;
    // Al cambiar a Mes, por defecto usar el mes en curso
    if (range === 'month') this.monthOffset = 0;
    this.page = 1;
    try {
      localStorage.setItem(this.LS_KEYS.ordersRange, this.selectedRange);
      localStorage.setItem(this.LS_KEYS.ordersMonthOffset, String(this.monthOffset));
    } catch { }
    this.fetchOrders();
  }

  // Cambiar entre Mes actual (0) y Mes anterior (-1)
  setMonthOffset(offset: 0 | -1) {
    if (this.monthOffset === offset) return;
    this.monthOffset = offset;
    // Mantener el rango seleccionado como 'month'
    this.selectedRange = 'month';
    this.page = 1;
    try {
      localStorage.setItem(this.LS_KEYS.ordersRange, this.selectedRange);
      localStorage.setItem(this.LS_KEYS.ordersMonthOffset, String(this.monthOffset));
    } catch { }
    this.fetchOrders();
  }

  // --- Filtro por tipo de mesa ---
  setActiveType(type: 'ALL' | 'LOCAL' | 'DELIVERY' | 'PICKUP' | 'FOODTRUCK'): void {
    // Si se vuelve a pulsar el mismo tipo, se desactiva el filtro (ALL)
    if (this.activeType === type) {
      this.activeType = 'ALL';
    } else {
      this.activeType = type;
    }
    try { localStorage.setItem(this.LS_KEYS.tableType, this.activeType); } catch { }
    this.rebuildDatasets(this.orderData || []);
  }

  private normalizeTableType(raw?: string, tableName?: string): 'LOCAL' | 'DELIVERY' | 'PICKUP' | 'FOODTRUCK' {
    const s = String(raw || '').toUpperCase();
    if (s.includes('FOODTRUCK') || s.includes('FOOD-TRUCK') || s.includes('FOOD TRUCK')) return 'FOODTRUCK';
    if (s.includes('DELIVERY') || s.includes('ENVIO') || s.includes('REPARTO')) return 'DELIVERY';
    if (s.includes('PICKUP')) return 'PICKUP';
    const lname = String(tableName || '').toLowerCase();
    if (lname.includes('foodtruck') || lname.includes('food-truck') || lname.includes('food truck')) return 'FOODTRUCK';
    if (lname.includes('delivery') || lname.includes('envio') || lname.includes('reparto')) return 'DELIVERY';
    if (lname.includes('pickup')) return 'PICKUP';
    return 'LOCAL';
  }

  private applyTypeFilter(list: any[]): any[] {
    const src = list || [];
    if (this.activeType === 'ALL') return src;
    const filtered = src.filter((o: any) => this.normalizeTableType(o?.table?.type, o?.table?.name) === this.activeType);
    return filtered.length ? filtered : src;
  }

  private startOfLocalDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  private addDays(d: Date, days: number): Date {
    const x = new Date(d);
    x.setDate(x.getDate() + days);
    return x;
  }

  // Monday as start of week
  private currentWeekRangeLocal(): { from: Date; to: Date } {
    const now = new Date();
    const day = now.getDay(); // 0=Sun..6=Sat
    const mondayDelta = (day + 6) % 7; // days since Monday
    const start = this.startOfLocalDay(this.addDays(now, -mondayDelta));
    const end = this.addDays(start, 7);
    return { from: start, to: end };
  }

  private currentMonthRangeLocal(): { from: Date; to: Date } {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return { from: start, to: end };
  }

  // Rango de mes con desplazamiento: 0 = actual, -1 = anterior
  private monthRangeLocal(offset: number): { from: Date; to: Date } {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 1);
    return { from: start, to: end };
  }

  private currentDayRangeLocal(): { from: Date; to: Date } {
    const start = this.startOfLocalDay(new Date());
    const end = this.addDays(start, 1);
    return { from: start, to: end };
  }

  private getUtcRangeISO(): { from: string; to: string } {
    let range: { from: Date; to: Date };
    if (this.selectedRange === 'week') range = this.currentWeekRangeLocal();
    else if (this.selectedRange === 'month') range = this.monthRangeLocal(this.monthOffset || 0);
    else range = this.currentDayRangeLocal();
    return { from: range.from.toISOString(), to: range.to.toISOString() };
  }

  private normalizeApiOrders(data: any): any[] {
    const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
    const rootProducts: any[] = Array.isArray((data as any)?.productsSummary) ? (data as any).productsSummary : [];
    return (list || []).map((entry: any) => {
      const raw = entry && typeof entry === 'object' && (entry as any).order ? (entry as any).order : entry;
      const uuid: string = raw?.uuid || '';
      const numericId = raw?.id;
      const tableUuid: string | null = raw?.tableUuid ?? raw?.table?.uuid ?? null;
      const tableName: string = raw?.table?.name ?? (tableUuid ? 'Mesa' : 'Sin mesa');
      const tableTypeRaw: string = raw?.table?.type ?? raw?.tableType ?? raw?.table?.typeSlug ?? raw?.orderType ?? '';
      const tableType: string = (tableTypeRaw || '').toString().toUpperCase();
      const createdAt: string = raw?.createdAt || raw?.date || new Date().toISOString();
      const statusRaw: string = raw?.statusSlug || raw?.status || raw?.statusName || '';
      const status = this.toCanonicalStatus(statusRaw);

      let items: any[] = [];
      if (Array.isArray(raw?.items)) {
        items = raw.items;
      } else if (Array.isArray(raw?.orderDetail)) {
        items = raw.orderDetail;
      } else if (Array.isArray(raw?.menuProducts)) {
        const mpItems: any[] = [];
        raw.menuProducts.forEach((mp: any) => {
          const baseMp = mp || {};
          const groupItems: any[] = Array.isArray(baseMp.items) ? baseMp.items : [];
          groupItems.forEach((it: any) => {
            mpItems.push({
              ...it,
              menuProductUuid: it?.menuProductUuid || baseMp?.menuProduct?.uuid || baseMp?.product?.uuid || null,
              menuProduct: baseMp?.menuProduct || null,
              product: baseMp?.product || null,
            });
          });
        });
        items = mpItems;
      }

      const orderHashId: string = String(raw?.hashId ?? raw?.orderHashId ?? uuid ?? '') || '';
      const computedTotal = items.reduce((acc: number, it: any) => acc + Number(it?.qty || 0) * Number(it?.price || 0), 0);
      const amount: number = Number(raw?.totalAmount) || Number(raw?.amount) || computedTotal || 0;
      const counts = { pending: 0, sent: 0, ready: 0, delivered: 0, canceled: 0 };
      items.forEach((it: any) => {
        const s = (it?.itemStatusSlug || it?.itemStatus?.slug || '').toLowerCase();
        if (s === 'pending') counts.pending++;
        else if (s === 'sent') counts.sent++;
        else if (s === 'ready') counts.ready++;
        else if (s === 'delivered') counts.delivered++;
        else if (s === 'canceled') counts.canceled++;
      });
      const itemSummary = `P:${counts.pending} • S:${counts.sent} • R:${counts.ready} • D:${counts.delivered} • C:${counts.canceled}`;

      // Compact product summary (e.g., "2× Burger, 1× Pizza, +2 más")
      const productQtyByName = new Map<string, number>();
      items.forEach((it: any) => {
        const name = String(it?.menuProduct?.name || it?.product?.name || it?.name || 'Producto').trim();
        const qty = Number(it?.qty || 0) || 0;
        if (!name) return;
        productQtyByName.set(name, (productQtyByName.get(name) || 0) + qty);
      });
      let productSummary = '';
      const sortedProducts = Array.from(productQtyByName.entries()).sort((a, b) => (b[1] - a[1]));
      if (sortedProducts.length > 0) {
        const top3 = sortedProducts.slice(0, 3).map(([name, qty]) => `${qty}× ${name}`);
        const extraCount = Math.max(0, sortedProducts.length - top3.length);
        productSummary = top3.join(', ') + (extraCount > 0 ? `, +${extraCount} más` : '');
      } else if (rootProducts.length > 0 && list.length === 1) {
        // Fallback: usar productsSummary del backend cuando no hay items en la orden
        const sorted = [...rootProducts].sort((a: any, b: any) => (Number(b?.qty || 0) - Number(a?.qty || 0)));
        const top3 = sorted.slice(0, 3).map((p: any) => `${Number(p?.qty || 0)}× ${p?.name}`);
        const extraCount = Math.max(0, sorted.length - top3.length);
        productSummary = top3.join(', ') + (extraCount > 0 ? `, +${extraCount} más` : '');
      }

      // Payment summary: sum of payments and methods if present
      const paymentsRaw = Array.isArray(raw?.payments) ? raw.payments : [];
      // Prefer payment.paidTotal if provided; otherwise sum each payment line.
      const paymentObj = raw?.payment || {};
      const paidFromObj: number = Number(paymentObj?.paidTotal ?? 0) || 0;
      const paidFromLines: number = paymentsRaw.reduce((acc: number, p: any) => {
        // If a unified amount exists, use it; otherwise sum cash/card split.
        const unified = p?.amount !== undefined ? Number(p.amount) : NaN;
        const linePaid = !isNaN(unified) && unified > 0
          ? unified
          : (Number(p?.cashAmount || 0) + Number(p?.cardAmount || 0));
        return acc + (isNaN(linePaid) ? 0 : linePaid);
      }, 0);
      const paidAmount = paidFromObj > 0 ? paidFromObj : paidFromLines;
      const methodsSet = new Set<string>();
      paymentsRaw.forEach((p: any) => {
        const m = String(p?.paymentMethod || p?.method || '').toLowerCase();
        if (m) methodsSet.add(m);
        if (Number(p?.cashAmount || 0) > 0) methodsSet.add('cash');
        if (Number(p?.cardAmount || 0) > 0) methodsSet.add('card');
        if (Number(p?.agreementAmount || 0) > 0) methodsSet.add('agreement');
      });
      // Price breakdown: prefer fields from payment when available
      const itemsSubtotal: number = Number(paymentObj?.itemsSubtotal ?? amount) || 0;
      const discountAmount: number = Number(paymentObj?.discount ?? raw?.discountAmount ?? raw?.discount ?? raw?.discountValue ?? 0) || 0;
      const tipAmount: number = Number(paymentObj?.tip ?? raw?.tipAmount ?? raw?.tip ?? raw?.serviceCharge ?? raw?.propina ?? 0) || 0;
      // Delivery puede venir dentro de payment o al nivel raíz
      const deliveryAmount: number = Number(paymentObj?.delivery ?? paymentObj?.deliveryFee ?? raw?.deliveryAmount ?? raw?.delivery ?? raw?.deliveryFee ?? raw?.deliveryCost ?? 0) || 0;
      const payableAmount: number = Number(paymentObj?.totalDue ?? (itemsSubtotal - discountAmount + tipAmount)) || 0;
      const pendingCalc: number = Number(paymentObj?.remaining ?? (payableAmount - paidAmount));
      const paymentSummary = {
        paid: paidAmount,
        pending: Math.max(0, pendingCalc || 0),
        methods: Array.from(methodsSet)
      };
      // Build a tabulable items list for the card, including aggregated qty/total and selected options
      // Contract: OrderItemDto (items) + OrderItemOptionDto (selectedOptions)
      let itemsList = items.map((it: any) => {
        const name = String(
          it?.menuProduct?.name
          || it?.product?.name
          || it?.name
          || 'Producto'
        );
        // Quantity: use selectedOptionsCount when present, otherwise qty
        const baseQty = Number(it?.qty ?? 0) || 0;
        const rawOptions: any[] = Array.isArray(it?.selectedOptions)
          ? it.selectedOptions
          : (Array.isArray(it?.optionSnapshots)
            ? it.optionSnapshots
            : (Array.isArray(it?.options) ? it.options : []));
        const qtyAgg: number = Number(
          it?.selectedOptionsCount ?? it?.totalItems ?? baseQty
        ) || 0;
        // Price base: unit price from dto
        const priceBase: number = Number(
          it?.price
          ?? it?.product?.price
          ?? it?.menuProduct?.sellPrice
          ?? 0
        ) || 0;
        // Line total: prefer aggregated sell price totals when provided
        const lineTotal: number = Number(
          it?.selectedOptionsSellPriceTotal
          ?? it?.totalSellPrice
          ?? (priceBase * qtyAgg)
        ) || 0;

        // Options breakdown: collapse selected options by name with quantities
        let optionsList: Array<{ name: string; qty: number }> = [];
        if (Array.isArray(rawOptions) && rawOptions.length > 0) {
          const counter = new Map<string, number>();
          rawOptions.forEach((opt: any) => {
            const oname = String(
              opt?.nameSnapshot
              ?? opt?.name
              ?? opt?.optionName
              ?? opt?.menuProductOption?.name
              ?? ''
            ).trim();
            if (!oname) return;
            const oq = Number(opt?.qty ?? opt?.quantity ?? 1) || 1;
            counter.set(oname, (counter.get(oname) || 0) + oq);
          });
          optionsList = Array.from(counter.entries()).map(([n, q]) => ({ name: n, qty: q }));
          optionsList.sort((a, b) => a.name.localeCompare(b.name));
        }

        const status = String(it?.itemStatusSlug || it?.status || '').toLowerCase() || 'pending';
        return {
          name,
          qty: qtyAgg,
          price: priceBase,
          status,
          options: optionsList,
          lineTotal,
        } as any;
      }).filter((it: any) => (it.status || '').toLowerCase() !== 'canceled');

      // Fallback: si la API sólo entrega productsSummary (sin items detallados), usarlo para poblar itemsList
      if ((!itemsList || itemsList.length === 0) && rootProducts.length > 0 && list.length === 1) {
        itemsList = rootProducts.map((p: any) => ({
          name: String(p?.name || 'Producto'),
          qty: Number(p?.qty || 0) || 0,
          price: Number(p?.unitPrice || 0) || 0,
          status: '',
          options: [],
          lineTotal: Number(p?.total || 0) || 0,
        }));
      }
      // Customer name if present (may be string or object)
      let customerName: string = '';
      const cnRaw: any = raw?.clientName ?? raw?.customerName ?? raw?.customer ?? raw?.client;
      if (typeof cnRaw === 'string') {
        customerName = cnRaw;
      } else if (cnRaw && typeof cnRaw === 'object') {
        customerName = (cnRaw.name ?? cnRaw.fullName ?? cnRaw.title ?? '');
      }
      customerName = (customerName || '').toString().trim();

      const orderIdStr = numericId !== undefined && numericId !== null
        ? String(numericId)
        : String((uuid || '').slice(-4));

      return {
        id: orderIdStr,
        orderId: orderIdStr,
        order: { id: orderIdStr, uuid },
        date: createdAt,
        totalItems: items.length,
        table: { name: tableName, uuid: tableUuid, type: tableType },
        location: tableName,
        status,
        amount,
        itemSummary,
        itemCounts: counts,
        productSummary,
        paymentSummary,
        priceSummary: { total: itemsSubtotal, discount: discountAmount, tip: tipAmount, payable: payableAmount, delivery: deliveryAmount },
        itemsList,
        customerName,
        orderHashId,
      };
    });
  }

  private fetchOrders() {
    // Para Mes, usar from/to explícitos (permite mes actual o anterior)
    let params: any = { page: this.page, size: this.pageSize };
    if (this.selectedRange === 'month') {
      const { from, to } = this.getUtcRangeISO();
      params = { ...params, from, to };
    } else {
      // Usar backend range param: day|week
      const rangeParam: 'day' | 'week' = (this.selectedRange === 'today' ? 'day' : 'week');
      params = { ...params, range: rangeParam };
    }
    this.isLoading = true;
    this.orderService.getOrders(params).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((resp: any) => {
      // Detect server-side pagination using meta.pagination or top-level fields
      const meta = (resp?.meta?.pagination) || resp?.pagination || resp?.pageInfo || {};
      const hasServerPaging = resp && typeof resp === 'object' && (
        Array.isArray(resp?.data) || Array.isArray(resp)
      ) && (
          meta?.total !== undefined || meta?.pageCount !== undefined || resp?.total !== undefined || resp?.totalPages !== undefined
        );
      this.serverPaginated = !!hasServerPaging;
      const mapped = this.normalizeApiOrders(resp);
      this.orderData = mapped;
      this.rebuildDatasets(mapped);
    }, (err) => {
      console.error('Error fetching orders with params', params, err);
      // graceful fallback
      this.serverPaginated = false;
      this.allData = this.paginator(this.orderData, this.page, this.totalRows);
    });
  }

  private rebuildDatasets(mapped: any[]): void {
    const filteredByType = this.applyTypeFilter(mapped || []);
    const size = this.pageSize;
    const total = filteredByType.length;
    const totalPages = Math.max(1, Math.ceil(total / Math.max(1, this.totalRows)));
    this.totalPage = totalPages;

    const byStatus = (s: 'OPEN' | 'CLOSED' | 'PENDING PAYMENT' | 'ALL') =>
      s === 'ALL' ? filteredByType : filteredByType.filter((o: any) => o?.status === s);

    // Paginación del lado del cliente para cada dataset
    this.allData = this.paginator(byStatus('ALL'), this.page, this.totalRows);
    this.openData = this.paginator(byStatus('OPEN'), this.page, this.totalRows);
    this.closedData = this.paginator(byStatus('CLOSED'), this.page, this.totalRows);
    this.pendingPaymentData = this.paginator(byStatus('PENDING PAYMENT'), this.page, this.totalRows);
  }

  private toCanonicalStatus(val: string): string {
    if (!val) return '';
    const s = String(val).toLowerCase();
    if (s.includes('abiert')) return 'OPEN';
    if (s.includes('cerrad')) return 'CLOSED';
    if (s.includes('pagar') || s.includes('payment')) return 'PENDING PAYMENT';
    if (s.includes('cancel')) return 'CANCELLED';
    return val.toUpperCase();
  }
}

function compare(a: number | string, b: number | string, isAsc: boolean) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}